<template>
    <VcModal
        data-test-name="global-confirm-modal"
        :visible="isOpen"
        :width="MODAL_WIDTH"
        :header="header || defaultHeader"
        wrapper-classes="confirm-modal"
        append-to-body
        @close="closeModal"
    >
        <div class="my-8 flex flex-col items-center justify-center">
            <div
                v-if="confirmModalState.descriptionIcon"
                class="mb-5"
            >
                <RedesignBaseIcon
                    :name="confirmModalState.descriptionIcon"
                    :size="64"
                    class="w-16 h-16 text-secondary-actions"
                />
            </div>
            <div
                data-testid="confirm-modal-message"
                class="text-center main-text-color text-base max-w-65-p break-normal"
            >
                {{ message || defaultDescription }}
            </div>
        </div>
        <template #footer>
            <div class="w-full flex items-center justify-between">
                <VcButton
                    data-test-name="confirm-modal-cancel-button"
                    :type="currentTypeData.cancelType || 'outline'"
                    :color="currentTypeData.cancelColor || 'secondary'"
                    :icon="currentTypeData.iconCancel"
                    @click="closeModal"
                >
                    {{ t(currentTypeData.cancelActionKey) }}
                </VcButton>
                <VcButton
                    data-test-name="confirm-modal-confirm-button"
                    :type="currentTypeData.type"
                    :color="currentTypeData.color"
                    :icon="currentTypeData.iconConfirm"
                    @click="confirm"
                >
                    {{ t(currentTypeData.actionKey) }}
                </VcButton>
            </div>
        </template>
    </VcModal>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { confirmModalState, ConfirmServiceTypeType } from '@/composables/useConfirmModal'
import { VcButton, VcModal } from '@voicenter-team/voicenter-ui-plus'
import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'

const { t } = useI18n()

const MODAL_WIDTH = '456px'

interface ModalTypeData {
    color: string
    type?: string
    cancelType?: string
    cancelColor?: string
    iconConfirm: string
    iconCancel: string
    actionKey: string
    cancelActionKey: string
}

const MODAL_DATA_DEFAULT: Record<ConfirmServiceTypeType, ModalTypeData> = {
    confirm: {
        color: 'primary',
        iconConfirm: 'check',
        iconCancel: 'x',
        actionKey: 'common.confirm',
        cancelActionKey: 'common.cancel'
    },
    save: {
        color: 'primary',
        iconConfirm: 'save',
        iconCancel: 'x',
        actionKey: 'common.save',
        cancelActionKey: 'common.cancel'
    },
    edit: {
        color: 'primary',
        iconConfirm: 'check',
        iconCancel: 'x',
        actionKey: 'common.confirm',
        cancelActionKey: 'common.cancel'
    },
    delete: {
        color: 'destructive',
        iconConfirm: 'vc-icon-recycle-bin',
        iconCancel: 'x',
        actionKey: 'common.delete',
        cancelActionKey: 'common.cancel'
    },
    discard: {
        color: 'destructive',
        type: 'outline',
        cancelType: 'default',
        cancelColor: 'primary',
        iconConfirm: '',
        iconCancel: '',
        actionKey: 'dialog.discardChanges.discard',
        cancelActionKey: 'dialog.discardChanges.keepEditing'
    }
}

const currentTypeData = computed(() => {
    const _type = confirmModalState.value.type as ConfirmServiceTypeType
    return MODAL_DATA_DEFAULT[_type] || MODAL_DATA_DEFAULT.confirm
})

const isOpen = computed(() => {
    return confirmModalState.value.open
})
const header = computed(() => {
    return confirmModalState.value.header
})
const message = computed(() => {
    return confirmModalState.value.message
})
const defaultHeader = computed(() => {
    return t('general.defaultConfirmation')
})
const defaultDescription = computed(() => {
    return t('dialog.confirm.description')
})

const emit = defineEmits<{
    (e: 'close'): void
    (e: 'confirm'): void
}>()

function closeModal () {
    if (confirmModalState.value.onClose !== undefined) {
        confirmModalState.value.onClose()
    }

    emit('close')
}
function confirm () {
    if (confirmModalState.value.onConfirm !== undefined) {
        confirmModalState.value.onConfirm()
    }
    emit('confirm')
}
</script>
